import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getMeasurementUnitDetails, createMeasurementUnit, updateMeasurementUnit } from '../../Controller/MeasurementUnitController';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import { withNamespaces } from 'react-i18next';

/**
 * Shared form component for creating and editing measurement units
 * @param {Object} props - Component props
 * @returns {JSX.Element}
 */
const MeasurementUnitForm = ({ isEdit = false, slack = null, ...props }) => {
  const { t, i18n } = props;
  const history = useHistory();
  const { lng } = useParams();

  // Form state
  const [formData, setFormData] = useState({
    label: '',
    unit_code: '',
    symbol: '',
    unit_type: '',
    dimension_factor: '1',
    is_not_applicable: false,
    allow_as_base_unit: '1',
    allow_decimal_quantity: '1',
    status: '1'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  // Fetch measurement unit details if in edit mode
  useEffect(() => {
    if (isEdit && slack) {
      fetchMeasurementUnitDetails();
    }
  }, [isEdit, slack]);

  /**
   * Fetch measurement unit details for edit mode
   */
  const fetchMeasurementUnitDetails = async () => {
    setInitialLoading(true);
    try {
      const response = await getMeasurementUnitDetails(slack);

      if (response.success && response.data) {
        const unitType = response.data.uom_type || response.data.unit_type || '';
        const dimensionFactor = response.data.dimension_factor;
        const isNotApplicable = response.data.is_not_applicable || dimensionFactor === null || dimensionFactor === undefined || dimensionFactor === '';
        const allowAsBase = response.data.is_allowed_as_base_uom !== undefined
          ? String(response.data.is_allowed_as_base_uom === true || response.data.is_allowed_as_base_uom === 1 || response.data.is_allowed_as_base_uom === '1' ? 1 : 0)
          : (response.data.allow_as_base_unit !== undefined ? String(response.data.allow_as_base_unit) : '1');
        const allowDecimal = response.data.is_decimal !== undefined
          ? String(response.data.is_decimal === true || response.data.is_decimal === 1 || response.data.is_decimal === '1' ? 1 : 0)
          : (response.data.allow_decimal_quantity !== undefined ? String(response.data.allow_decimal_quantity) : '1');

        setFormData({
          label: response.data.label || '',
          unit_code: response.data.unit_code || '',
          symbol: response.data.symbol || '',
          unit_type: unitType,
          dimension_factor: dimensionFactor || '',
          is_not_applicable: isNotApplicable,
          allow_as_base_unit: allowAsBase,
          allow_decimal_quantity: allowDecimal,
          status: response.data.status === 1 || response.data.status === '1' ? '1' : '0'
        });
      } else {
        Utility.toastNotifications(response.message || t('measurementUnits.form.errors.failedToFetchDetails'), 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching measurement unit details:', error);
      Utility.toastNotifications(t('measurementUnits.form.errors.errorFetchingDetails'), 'Error', 'error');
    } finally {
      setInitialLoading(false);
    }
  };

  /**
   * Handle input change
   * @param {Event} e - Input change event
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  /**
   * Validate form inputs
   * @returns {boolean} - True if form is valid
   */
  const validateForm = () => {
    const newErrors = {};

    // Validate label field
    if (!formData.label.trim()) {
      newErrors.label = t('measurementUnits.form.validation.labelRequired') || 'Label is required';
    }

    // Validate unit_code field
    if (!formData.unit_code.trim()) {
      newErrors.unit_code = t('measurementUnits.form.validation.unitCodeRequired') || 'Unit code is required';
    }

    // Validate unit_type field
    if (!formData.unit_type) {
      newErrors.unit_type = 'Unit type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Form submission started");
    const isValid = validateForm();
    console.log("Validation result:", isValid);

    if (!isValid) {
      console.log("Validation failed, stopping submission");
      return;
    }

    console.log("Validation passed, calling API");
    setLoading(true);
    try {
      let response;

      if (isEdit) {
        response = await updateMeasurementUnit(slack, formData);
      } else {
        response = await createMeasurementUnit(formData);
      }

      if (response.success) {
        Utility.toastNotifications(
          response.message || t(`measurementUnits.form.success.${isEdit ? 'updated' : 'created'}`),
          'Success',
          'success'
        );
        // Navigate back to list page
        history.push(`/${lng}/settings/measurement-units`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} measurement unit:`, error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel button click
   */
  const handleCancel = () => {
    history.push(`/${lng}/settings/measurement-units`);
  };

  return (
    <Card style={{ border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <Card.Body className="p-3">
        {initialLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" variant="primary" size="sm">
              <span className="visually-hidden">{t('measurementUnits.form.loading')}</span>
            </Spinner>
            <p className="mt-2 mb-0" style={{ fontSize: '0.85rem' }}>{t('measurementUnits.form.loadingDetails')}</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row className="g-2">
              {/* Row 1: Label & Unit Code */}
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {t('measurementUnits.form.labels.label') || 'Label'} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    isInvalid={!!errors.label}
                    placeholder={t('measurementUnits.form.placeholders.label') || 'Enter measurement unit label'}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.label}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('measurementUnits.form.helpText.labelExample') || 'Example: Kilograms, Liters, Pieces, etc.'}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {t('measurementUnits.form.labels.unitCode') || 'Unit Code'} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="unit_code"
                    value={formData.unit_code}
                    onChange={handleChange}
                    isInvalid={!!errors.unit_code}
                    placeholder={t('measurementUnits.form.placeholders.unitCode') || 'Enter unit code (e.g., KG, L, PCS)'}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.unit_code}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {t('measurementUnits.form.helpText.unitCodeExample') || 'Short code for the unit (2-5 characters recommended)'}
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Row 2: Symbol & Unit Type */}
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Symbol
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="symbol"
                    value={formData.symbol}
                    onChange={handleChange}
                    placeholder="Enter symbol (e.g., kg, L, pcs)"
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Short symbol to represent the unit (optional)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Unit Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="unit_type"
                    value={formData.unit_type}
                    onChange={handleChange}
                    isInvalid={!!errors.unit_type}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  >
                    <option value="">Select unit type</option>
                    <option value="WEIGHT">WEIGHT</option>
                    <option value="VOLUME">VOLUME</option>
                    <option value="COUNT">COUNT</option>
                    <option value="LENGTH">LENGTH</option>
                    <option value="AREA">AREA</option>
                    <option value="PACKAGING">PACKAGING</option>
                    <option value="OTHER">OTHER</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.unit_type}
                  </Form.Control.Feedback>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Category of measurement unit
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Row 3: Dimension Factor & Not Applicable Checkbox */}
              {/* <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Dimension Factor (Base Unit Conversion){' '}
                    <FontAwesomeIcon icon={faInfoCircle} className="text-primary ms-1" style={{ cursor: 'pointer', fontSize: '0.8rem' }} title="Conversion ratio to base unit" />
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="dimension_factor"
                    value={formData.dimension_factor}
                    onChange={handleChange}
                    disabled={formData.is_not_applicable}
                    placeholder="Enter dimension factor (e.g., 1, 0.001)"
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  />
                  <Form.Text style={{ fontSize: '0.75rem', color: '#2563eb' }}>
                    Example: For weight -&gt; KG = 1, GM = 0.001. Leave blank for packaging units like BAG, BOX, CASE.
                  </Form.Text>
                </Form.Group>
              </Col> */}

              <Col md={6} className="d-flex align-items-center">
                <Form.Group className="mb-2 pt-md-3">
                  <Form.Check
                    type="checkbox"
                    id="is_not_applicable"
                    name="is_not_applicable"
                    label="Not applicable (Packaging/Custom)"
                    checked={formData.is_not_applicable}
                    onChange={handleChange}
                    style={{ fontWeight: '500', color: '#334155', fontSize: '0.875rem' }}
                  />
                  <Form.Text className="text-muted d-block ms-4" style={{ fontSize: '0.75rem' }}>
                    Check if this unit does not have a fixed conversion factor
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Row 4: Allow as Base Unit & Allow Decimal Quantity */}
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Allow as Base Unit{' '}
                    <FontAwesomeIcon icon={faInfoCircle} className="text-primary ms-1" style={{ cursor: 'pointer', fontSize: '0.8rem' }} title="Can this unit be selected as a base unit?" />
                  </Form.Label>
                  <Form.Select
                    name="allow_as_base_unit"
                    value={formData.allow_as_base_unit}
                    onChange={handleChange}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Can this unit be selected as a base unit for products?
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Allow Decimal Quantity{' '}
                    <FontAwesomeIcon icon={faInfoCircle} className="text-primary ms-1" style={{ cursor: 'pointer', fontSize: '0.8rem' }} title="Can quantities be in decimal?" />
                  </Form.Label>
                  <Form.Select
                    name="allow_decimal_quantity"
                    value={formData.allow_decimal_quantity}
                    onChange={handleChange}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </Form.Select>
                  <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Can quantities be in decimal for this unit?
                  </Form.Text>
                </Form.Group>
              </Col>

              {/* Row 5: Status */}
              <Col md={6}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontWeight: '500', color: '#334155', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    {t('measurementUnits.form.labels.status') || 'Status'}
                  </Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    style={{ borderRadius: '6px', padding: '0.375rem 0.625rem', fontSize: '0.875rem' }}
                  >
                    <option value="1">{t('measurementUnits.form.status.active') || 'Active'}</option>
                    <option value="0">{t('measurementUnits.form.status.inactive') || 'Inactive'}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3 pt-2">
              <Button variant="secondary" onClick={handleCancel} className="me-2" disabled={loading} style={{ borderRadius: '6px', padding: '0.375rem 1rem', fontSize: '0.875rem' }}>
                <FontAwesomeIcon icon={faTimes} className="me-1" /> {t('measurementUnits.form.buttons.cancel') || 'Cancel'}
              </Button>
              <Button variant="primary" type="submit" disabled={loading} style={{ borderRadius: '6px', padding: '0.375rem 1rem', fontSize: '0.875rem' }}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                    {isEdit ? (t('measurementUnits.form.buttons.updating') || 'Updating...') : (t('measurementUnits.form.buttons.saving') || 'Saving...')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-1" /> {isEdit ? (t('measurementUnits.form.buttons.update') || 'Update') : (t('measurementUnits.form.buttons.save') || 'Save')}
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default withNamespaces()(MeasurementUnitForm);
